import { deleteUndefined } from "Helpers/FirestoreHelper";
import { FirestoreWhere } from "Store/types/FirestoreWhere";
import { ValidData } from "Store/types/ValidData";
import {
	CollectionReference,
	DocumentData,
	deleteDoc,
	doc,
	onSnapshot as firebaseOnSnapshot,
	query,
	setDoc,
	updateDoc,
	where,
} from "firebase/firestore";
import { StoreApi, UseBoundStore, create } from "zustand";
import { devLog } from "utils/developmentLogger";

type CollectionRef = CollectionReference<DocumentData, DocumentData>;

interface State<T> {
	isInitializing: boolean;
	error?: Error;
	items: (T & ValidData)[];
	ref?: CollectionRef;
	subscribe: (ref: CollectionRef, wheres?: FirestoreWhere[]) => () => void;
	setItem: (item: T & ValidData) => Promise<void>;
	deleteItem: (item: T & ValidData) => Promise<void>;
	updateItem: (item: Partial<T> & ValidData) => Promise<void>;
	reset: () => void;
}

interface Props<T> {
	is: (object: any) => object is T;
	sort: (a: T, b: T) => number;
	onSnapshot?: (items: (T & ValidData)[]) => void;
	softDelete?: Partial<T>;
}

export function createCollectionStore<T>(
	props: Props<T>
): UseBoundStore<StoreApi<State<T>>> {
	const { is, sort, softDelete, onSnapshot } = props;

	return create<State<T>>((set, get) => ({
		isInitializing: true,
		items: [],
		subscribe: (ref, wheres) => {
			set({ ref });

			return firebaseOnSnapshot(
				wheres ? query(ref, ...wheres.map((w) => where(...w))) : ref,
				(snapshot) => {
					const items = snapshot.docs
						.reduce<(T & ValidData)[]>((result, doc) => {
							const docData = doc.data();
							if (is(docData)) {
								result.push(docData as T & ValidData);
							}
							return result;
						}, [])
						.sort(sort);
					set({ items, error: undefined, isInitializing: false });
					void onSnapshot?.(items);
				},
				(error) => {
					devLog.error("Firestore collection error:", error);
					set({ error, isInitializing: false });
				}
			);
		},
		setItem: async (item) => {
			const { ref } = get();
			if (!ref) throw new Error("Cannot find reference.");

			await setDoc(doc(ref, item.id), deleteUndefined(item));
		},

		updateItem: async (item) => {
			const { ref } = get();
			if (!ref) throw new Error("Cannot find reference.");

			const previousItem = get().items.find((i) => i.id === item.id);
			if (previousItem) {
				Object.assign(previousItem, item);
				set({ items: [...get().items] });
			}

			await updateDoc(doc(ref, item.id), deleteUndefined(item, true));
		},
		deleteItem: async (item) => {
			const { ref } = get();
			if (!ref) throw new Error("Cannot find reference.");

			if (softDelete) {
				await updateDoc(doc(ref, item.id), {
					...softDelete,
				});
			} else {
				await deleteDoc(doc(ref, item.id));
			}
		},
		reset: () => {
			set({ items: [], error: undefined, isInitializing: true });
		},
	}));
}
